define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"csi",
	"csi/store/Nodes",
	"csi/table/Widget",
	"csi/utility/pathUtility",
	"csi/xml",
	"./ProductType"
], function(array, declare, csi, Nodes, Widget, {pathCreate}, xml, ProductType) {

	const selectAllNode = xml.nodeCreateClientNode();

	const _Nodes = declare(Nodes, {
		onResultUrls(resultUrls) {
			const firstUrl = resultUrls[0];

			if (!this.bFiltered && (firstUrl !== selectAllNode.$URL)) {
				this.add(selectAllNode, {before: firstUrl});
			}
		}
	});

	return declare(Widget, {
		/** @property {cnl} bColorsOnly - indicates if material supports only color (not size) */
		bColorsOnly: false,

		/** @property {cnl} bSizesOnly - indicates if material supports only size (not color) */
		bSizesOnly: false,

		/** @property {boolean} bWriteAccess - indicates if user has edit access for availability data */
		bWriteAccess: false,

		/** @property {cnl} selectAllUrl - client node url used to represent row in table to select all (or single column) */
		selectAllUrl: selectAllNode.$URL,

		/**
		 * @override
		 * Disable grouping due to select all row
		 */
		canGroup() {
			return false;
		},

		/**
		 * Return color in current context
		 * @param {Context} ctx 
		 * @return {cnl}
		 */
		getContextColor(ctx) {
			const url = this.bSizesOnly ? csi.EMPTY_URL : ctx.getNodeURL();
			return (url == this.selectAllUrl) ? csi.EMPTY_URL : url;
		},

		/**
		 * Return size in current context
		 * @param {Context} ctx 
		 * @return {cnl}
		 */
		getContextSize(ctx) {
			const url = this.bSizesOnly ? ctx.getNodeURL() : (ctx.getParam(ctx.PARAM_COLUMN_URL) || csi.EMPTY_URL);
			return (url == this.selectAllUrl) ? csi.EMPTY_URL : url;
		},

		/**
		 * Return index to exception stored for given color
		 * @param {C8Node} node - MaterialSeasonAvailability node
		 * @param {cnl} urlColor 
		 * @return {number}
		 */
		getColorException(node, urlColor) {
			return array.indexOf(node.AvailabilityColorExceptions, urlColor);
		},

		/**
		 * Return index to exception stored for given color and size
		 * @param {C8Node} node - MaterialSeasonAvailability node
		 * @param {cnl} urlColor 
		 * @param {cnl} urlSize 
		 * @return {number}
		 */
		getPairException(node, urlColor, urlSize) {
			const sizes = node.AvailabilityPairSizeExceptions;

			return csi.locate(node.AvailabilityPairColorExceptions, function(c, i) {
				return (c == urlColor) && (sizes[i] == urlSize);
			});
		},

		/**
		 * Return product
		 * @return {C8Node}
		*/
		getProduct() {
			const node = this.getViewNode();
			return this.cacheView.get(node?.AvailabilityProduct) || node;
		},

		/**
		 * Return index to exception stored for given size
		 * @param {C8Node} node - MaterialSeasonAvailability node 
		 * @param {cnl} urlColor 
		 * @return {number}
		 */
		getSizeException(node, urlSize) {
			return array.indexOf(node.AvailabilitySizeExceptions, urlSize);
		},

		/**
		 * Return true if product has colors
		 * @return {boolean}
		 */
		hasAvailabilityColors() {
			return !!this.getProduct()?.ProductColors.length;
		},

		/**
		 * Return true if product has sizes
		 * @return {boolean}
		 */
		hasAvailabilitySizes() {
			return !!this.getProduct()?.ProductSizes.length;
		},

		/**
		 * Control access to changing availability
		 * @param {Context} ctx 
		 * @param {Action} action 
		 * @return {boolean}
		 */
		onAccessAvailability(ctx, action) {
			return !!this.bWriteAccess;
		},

		/**
		 * @override
		 * Determine if user has right to edit availability
		 */
		setViewNode(node, bSaveSelections) {
			this.inherited(arguments);
			this.bWriteAccess = xml.nodeHasRight(node, xml.RIGHT_WRITE) && !this.getContext().isReadOnly();
		},

		/**
		 * @override
		 * Add path to product
		 */
		_onRootDependencyPaths(paths) {
			paths.push(pathCreate("AvailabilityProduct"));
			this.inherited(arguments);
		},

		/**
		 * @override
		 * If there are color and sizes, then add node to front to provide row for editing availability for all colors/sizes
		 */
		onCreateMainQueryLevel(ql) {
			if (this.hasAvailabilityColors() && this.hasAvailabilitySizes()) {
				ql.bOmitClientNodesFromCount = true;
				ql.storeClass = _Nodes;
			}
		},

		/**
		 * @override
		 * Configure view based on root node of view - root node could be of type ColorSizeAvailability (either MaterialSeasonAvailability or ProductSource)
		 * or Material (switchboard of ProductSource)
		 */
		_resolveView() {
			const {defView, relRoot} = this;
			const product = this.getProduct();

			// if material type has no color, then show table of sizes
			if (product && !ProductType.hasColor(product)) {
				relRoot.m_addPaths[0].m_path = "Child:AvailabilityProduct/Child:ProductSizes";
				relRoot.m_target = "ProductSize";
				relRoot.pathHints = null;
				relRoot.pathHintsContext = null;

				this.prefKey = defView.getKey("Size");
				this.bSizesOnly = true;
			} else if (product && !ProductType.hasSize(product)) {
				this.bColorsOnly = true;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Handle blank row in table count of results
		 */
		_setMainQueryLevelTotal(total) {
			const ql = this.getMainQueryLevel();

			if (ql?.bOmitClientNodesFromCount && !this._hasColumnFilter()) {
				total -= 1;
			}

			this.inherited(arguments);
		}
	});
});